import { apiClient } from "@/lib/api";
import {
  StandardResponse,
  PaginatedStandardResponse,
  StandardResponseWithMessage,
  ErrorResponse,
  isErrorResponse,
  PaginationInfo,
} from "@/types/api-response";

/**
 * Order statuses, mirroring the backend OrderStatus enum member for member.
 */
export const ORDER_STATUSES = [
  "Draft",
  "Pending",
  "Paid",
  "Processing",
  "Complete",
  "Cancelled",
  "Failed",
  "Refunded",
] as const;

export type OrderStatus = (typeof ORDER_STATUSES)[number];

/**
 * Order types, mirroring the backend OrderType enum member for member.
 *
 * `Transfer` (inbound domain transfer-in) and `Adjustment` (mid-cycle prorated add-on) were missing
 * here while the backend has had both: an order of either type serialised to a string this union
 * did not contain, so anything keying on it fell through its cases silently.
 */
export const ORDER_TYPES = [
  "NewService",
  "Renewal",
  "Upgrade",
  "Addon",
  "Transfer",
  "Adjustment",
  // A wallet top-up. Carries no OrderItem at all — OrderItem.ProductId is a required FK into
  // products, so an order for money paid into the balance has nothing to point at — which is why
  // every screen keying on an order's items needs a branch for this type rather than an empty table.
  "BalanceTopUp",
] as const;

export type OrderType = (typeof ORDER_TYPES)[number];

/**
 * The few order fields a screen needs to label a row it holds only an order id for — returned by
 * `POST /api/v1/admin/orders/summaries`. Mirrors the backend `OrderSummaryDto`.
 */
export interface OrderSummary {
  id: string;
  /** Human-readable order number, format WIC-ORD-XXXXXX. */
  orderNumber: string;
  type: OrderType;
}

/**
 * Order list item for admin view
 */
export interface OrderAdminListItem {
  id: string;
  orderNumber: string;
  tenantId: string;
  tenantName: string;
  userId: string;
  userEmail: string;
  userFullName?: string | null;
  userPhoneNumber?: string | null;
  type: OrderType;
  status: OrderStatus;
  itemsCount: number;
  total: number;
  /** VAT amount, already included in `total`. */
  tax: number;
  /** VAT rate the order was taxed at, as a percentage (10 means 10%). */
  taxRate: number;
  currency: string;
  placedAt: string;
  completedAt?: string;
  /** Bill code T-PayGate issued for this order's payment QR; null when there is none. */
  tPayGateBillCode?: string | null;
  /** A completed upgrade left parts the infrastructure did not apply. */
  upgradeNotApplied?: boolean;
}

/**
 * Order detail for admin view
 */
export interface OrderAdminDetail extends OrderAdminListItem {
  subtotal: number;
  discount: number;
  discountCode?: string;
  paymentId?: string;
  invoiceId?: string;
  cancelledAt?: string;
  cancellationReason?: string;
  notes?: string;
  items: OrderItem[];
  payment?: PaymentSummary;
  invoice?: InvoiceSummary;
}

/**
 * Order item details
 */
export interface OrderItem {
  id: string;
  productId: string;
  productName: string;
  resourceType: string;
  description: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  billingFrequency: "Hourly" | "Daily" | "Monthly" | "Quarterly" | "Annually";
  resourceId?: string;
  resourceAvailable: boolean;
  resourceName?: string;
  resourceStatus?: string;
  publicIpAddress?: string;
  /** Each part of an upgrade the infrastructure did not apply; empty otherwise. */
  notApplied?: OrderUpgradeNotApplied[];
}

/** A part of an upgrade that did not land: the vCPU/RAM change, a disk growth, or a disk it adds. */
export interface OrderUpgradeNotApplied {
  kind: "compute" | "disk" | "newDisk";
  device?: string | null;
  sizeGb?: number | null;
  cores?: number | null;
  memoryMb?: number | null;
}

/**
 * Payment summary info
 */
export interface PaymentSummary {
  id: string;
  orderNumber: string;
  /** The payment's own code (WIC-PAY-…), sent to T-PayGate as RefTransactionId. */
  paymentCode: string;
  /** Bill code T-PayGate issued for this payment's QR; null for any other payment. */
  tPayGateBillCode?: string | null;
  status: string;
  method: string;
  amount: number;
  createdAt: string;
}

/**
 * Invoice summary info
 */
export interface InvoiceSummary {
  id: string;
  invoiceNumber: string;
  issueDate: string;
  dueDate: string;
  status: string;
}

/**
 * Order statistics
 */
export interface OrderStatistics {
  totalOrders: number;
  pendingOrders: number;
  completedOrders: number;
  failedOrders: number;
  cancelledOrders: number;
  totalRevenue: number;
  currency: string;
  ordersByStatus: Record<string, number>;
  revenueByMonth: Record<string, number>;
}

/**
 * Query parameters for getting orders
 */
export interface GetOrdersParams {
  page?: number;
  pageSize?: number;
  status?: OrderStatus;
  tenantId?: string;
  userId?: string;
  /**
   * Placed-at range, already formatted for the API: the picked calendar day carrying this client's
   * UTC offset (`2026-08-24T00:00:00+07:00`). The server expands each end to the whole of that day
   * in the caller's frame — see GetOrdersQueryHandler.
   */
  fromDate?: string;
  toDate?: string;
  /** Matched against order number, user e-mail and user phone number. */
  keyword?: string;
}

/**
 * Complete order request
 */
export interface CompleteOrderRequest {
  paymentId: string;
  invoiceId: string;
}

/**
 * Cancel order request
 */
export interface CancelOrderRequest {
  reason: string;
}

/**
 * Admin Order Service
 * Handles all admin order management operations
 */
export const adminOrderService = {
  /**
   * Get orders with pagination and filters
   */
  async getOrders(
    params: GetOrdersParams,
  ): Promise<{ data: OrderAdminListItem[]; pagination: PaginationInfo }> {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        queryParams.append(key, value.toString());
      }
    });

    const response = await apiClient.get<
      PaginatedStandardResponse<OrderAdminListItem> | ErrorResponse
    >(`/api/v1/admin/orders?${queryParams.toString()}`);

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return {
      data: response.data.data,
      pagination: response.data.pagination,
    };
  },

  /**
   * Get order details by ID
   */
  async getOrderDetails(orderId: string): Promise<OrderAdminDetail> {
    const response = await apiClient.get<
      StandardResponse<OrderAdminDetail> | ErrorResponse
    >(`/api/v1/admin/orders/${orderId}`);

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return response.data.data;
  },

  /**
   * Resolve a batch of order ids to their number and type.
   *
   * POST because the ids are a list: a page of 100 GUIDs is ~3,700 characters of query string.
   * Ids matching no order are simply absent from the result, so callers map defensively — the
   * column this feeds is legitimately blank on rows funded by something other than an order.
   */
  async getOrdersByIds(orderIds: string[]): Promise<OrderSummary[]> {
    if (orderIds.length === 0) {
      return [];
    }

    const response = await apiClient.post<
      StandardResponse<OrderSummary[]> | ErrorResponse
    >("/api/v1/admin/orders/by-ids", { orderIds });

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return response.data.data;
  },

  /**
   * Get order statistics
   */
  async getStatistics(params: {
    fromDate?: string;
    toDate?: string;
    tenantId?: string;
  }): Promise<OrderStatistics> {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value) queryParams.append(key, value);
    });

    const response = await apiClient.get<
      StandardResponse<OrderStatistics> | ErrorResponse
    >(`/api/v1/admin/orders/statistics?${queryParams.toString()}`);

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return response.data.data;
  },

  /**
   * Manually complete an order
   */
  async manuallyCompleteOrder(
    orderId: string,
    request: CompleteOrderRequest,
  ): Promise<void> {
    const response = await apiClient.post<
      StandardResponseWithMessage<void> | ErrorResponse
    >(`/api/v1/admin/orders/${orderId}/complete`, request);

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }
  },

  /**
   * Cancel an order with reason
   */
  async cancelOrder(
    orderId: string,
    request: CancelOrderRequest,
  ): Promise<void> {
    const response = await apiClient.post<
      StandardResponseWithMessage<void> | ErrorResponse
    >(`/api/v1/admin/orders/${orderId}/cancel`, request);

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }
  },
};
